import React,{ memo, useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import Menu from '@inovua/reactdatagrid-community/packages/Menu';
import '@inovua/reactdatagrid-community/index.css';
import '@inovua/reactdatagrid-community/theme/default-dark.css';
import './productGrid.css';

import useDebouncedSearch from './hooks/useDebouncedSearch';
import useGridSelection from './hooks/useGridSelection';
import SearchBar from './components/SearchBar';
import BulkActionDropdown from './components/BulkActionDropdown';
import FilterDropdown from './components/FilterDropdown';
import { isAllSelected } from './utils/gridHelpers';
import { fontFamily } from '../../helpers/fontFamily';
import { loadColumnPreferences, applyColumnPreferences, updateColumnVisibility, updateColumnOrder, updateColumnWidth } from '../../utils/gridColumnPreferences';
import { getAutosizeColumnUpdates } from '../../utils/gridColumnAutosize';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZES = [20, 50, 100, 200];

const typeFilterToIsKit = (typeFilter) => {
  if (typeFilter === 'kits') return 1;
  if (typeFilter === 'products') return 0;
  return -1;
};

const ServerPaginatedDataGrid = memo(({
  columns,
  fetchData,
  searchPlaceholder = 'Search...',
  bulkActions = [],
  onBulkAction,
  onRowClick,
  idProperty = 'id',
  gridProps = {},
  refreshKey = 0,
  filterOptions = [],
  defaultFilter = 'active',
  typeFilterOptions = [],
  defaultTypeFilter = 'all',
  gridId = null,
  userInfo = null,
  toolbarRight = null,
  hideToolbar = false,
  externalSearch,
  resetKey,
  onFetchError,
  emptyText,
  onSelectedRowsChange
}) => {
  const { width: windowWidth } = useWindowDimensions();

  const {
    searchValue,
    debouncedValue,
    handleSearchChange,
    clearSearch
  } = useDebouncedSearch('', 500);

  const {
    selectionRef,
    selectedIds,
    selectedCount,
    hasSelection,
    handleSelectionChange,
    deselectAll
  } = useGridSelection();

  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeFilter, setActiveFilter] = useState(defaultFilter);
  const [activeTypeFilter, setActiveTypeFilter] = useState(defaultTypeFilter);
  const effectiveSearch = externalSearch !== undefined ? externalSearch : debouncedValue;
  const searchQueryRef = useRef(effectiveSearch);
  const filterRef = useRef(activeFilter);
  const typeFilterRef = useRef(activeTypeFilter);
  const fetchDataRef = useRef(fetchData);
  const onFetchErrorRef = useRef(onFetchError);
  const idPropertyRef = useRef(idProperty);
  onFetchErrorRef.current = onFetchError;
  idPropertyRef.current = idProperty;
  const gridRef = useRef(null);
  const prevRefreshKeyRef = useRef(refreshKey);

  useEffect(() => {
    searchQueryRef.current = effectiveSearch;
  }, [effectiveSearch]);

  useEffect(() => {
    filterRef.current = activeFilter;
  }, [activeFilter]);

  useEffect(() => {
    typeFilterRef.current = activeTypeFilter;
  }, [activeTypeFilter]);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentData, setCurrentData] = useState([]);
  const [fetchFailed, setFetchFailed] = useState(false);
  const currentDataRef = useRef([]);
  const totalCountRef = useRef(0);
  const rowCacheRef = useRef({});
  const onSelectedRowsChangeRef = useRef(onSelectedRowsChange);
  onSelectedRowsChangeRef.current = onSelectedRowsChange;
  const loadedPageRef = useRef(null);
  const [columnPreferencesRevision, setColumnPreferencesRevision] = useState(0);

  const clearSelection = useCallback(() => {
    deselectAll();
    if (onSelectedRowsChangeRef.current) {
      onSelectedRowsChangeRef.current([], { allSelected: false, count: 0 });
    }
  }, [deselectAll]);

  const dataSource = useCallback(async ({ skip, limit, sortInfo, filterValue }) => {
    const pageKey = JSON.stringify([skip, limit, sortInfo]);
    if (
      onSelectedRowsChangeRef.current &&
      loadedPageRef.current !== null &&
      loadedPageRef.current !== pageKey
    ) {
      clearSelection();
    }
    loadedPageRef.current = pageKey;

    setLoading(true);
    setError(null);
    setFetchFailed(false);

    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');

      const result = await fetchDataRef.current({
        skip,
        limit,
        sortInfo,
        filterValue,
        searchQuery: searchQueryRef.current,
        filter: filterRef.current,
        is_kit: typeFilterToIsKit(typeFilterRef.current),
        access_token,
        baseUrl: url
      });

      if (onFetchErrorRef.current && result && result.stale) {
        return { data: currentDataRef.current, count: totalCountRef.current };
      }

      const { data = [], count = 0 } = result || {};
      currentDataRef.current = data;
      totalCountRef.current = count;
      if (onSelectedRowsChangeRef.current) {
        data.forEach(row => {
          rowCacheRef.current[row[idPropertyRef.current]] = row;
        });
      }
      setTotalCount(count);
      setCurrentData(data);
      setLoading(false);

      return { data, count };
    } catch (err) {
      setLoading(false);
      if (onFetchErrorRef.current) {
        setFetchFailed(true);
        onFetchErrorRef.current(err);
        return { data: currentDataRef.current, count: totalCountRef.current };
      }
      setError(err.message || 'Failed to load data');
      return { data: [], count: 0 };
    }
  }, [effectiveSearch, activeFilter, activeTypeFilter, clearSelection]);

  const handleGridSelectionChange = useCallback((selection) => {
    handleSelectionChange(selection);
    if (!onSelectedRowsChangeRef.current) return;
    if (selection.selected === true) {
      const unselected = selection.unselected || {};
      const rows = currentDataRef.current.filter(
        row => !unselected[row[idPropertyRef.current]]
      );
      onSelectedRowsChangeRef.current(rows, {
        allSelected: true,
        count: totalCountRef.current - Object.keys(unselected).length
      });
      return;
    }
    const ids = Object.keys(selection.selected || {}).filter(id => selection.selected[id]);
    onSelectedRowsChangeRef.current(
      ids.map(id => rowCacheRef.current[id]).filter(Boolean),
      { allSelected: false, count: ids.length }
    );
  }, [handleSelectionChange]);

  useEffect(() => {
    clearSelection();
  }, [effectiveSearch, activeFilter, activeTypeFilter, resetKey, clearSelection]);

  const handleFilterChange = useCallback((newFilter) => {
    setActiveFilter(newFilter);
  }, []);

  const handleTypeFilterChange = useCallback((newTypeFilter) => {
    setActiveTypeFilter(newTypeFilter);
  }, []);

  // Handle refresh without remounting - use gridRef.reload() instead
  useEffect(() => {
    if (prevRefreshKeyRef.current !== refreshKey) {
      prevRefreshKeyRef.current = refreshKey;
      clearSelection();
      // Call reload() to refresh data without remounting the grid
      gridRef.current?.reload();
    }
  }, [refreshKey, clearSelection]);

  const handleBulkActionSelect = useCallback((actionType, option) => {
    if (!onBulkAction) return;

    let ids = [];

    if (selectedIds.allSelected) {
      onBulkAction({
        action: actionType,
        value: option.value,
        allSelected: true,
        unselectedIds: selectedIds.unselectedIds,
        totalCount,
        searchQuery: searchQueryRef.current,
        filter: filterRef.current,
        is_kit: typeFilterToIsKit(typeFilterRef.current)
      });
    } else {
      ids = selectedIds.selectedIds || [];
      onBulkAction({
        action: actionType,
        value: option.value,
        selectedIds: ids,
        allSelected: false
      });
    }
  }, [onBulkAction, selectedIds, totalCount]);

  const handleRowClick = useCallback((rowProps) => {
    if (onRowClick) {
      const rowData = rowProps.data;
      const currentIndex = currentData.findIndex(item => item[idProperty] === rowData[idProperty]);
      onRowClick(rowData, currentData, currentIndex >= 0 ? currentIndex : 0);
    }
  }, [onRowClick, currentData, idProperty]);

  const gridColumns = useMemo(() => {
    if (gridId && userInfo) {
      const preferences = loadColumnPreferences(gridId, userInfo);
      if (preferences) {
        return applyColumnPreferences(columns, preferences);
      }
    }
    return columns;
  }, [columns, gridId, userInfo, columnPreferencesRevision]);

  const handleColumnVisibleChange = useCallback(({ column, visible }) => {
    if (gridId && userInfo && column?.name) {
      updateColumnVisibility(gridId, userInfo, column.name, visible);
      setColumnPreferencesRevision(revision => revision + 1);
    }
  }, [gridId, userInfo]);

  const handleColumnOrderChange = useCallback((columnOrder) => {
    if (gridId && userInfo && Array.isArray(columnOrder)) {
      // Extract column names from the order
      const columnNames = columnOrder.map(col =>
        typeof col === 'string' ? col : col.name
      ).filter(Boolean);
      updateColumnOrder(gridId, userInfo, columnNames);
      setColumnPreferencesRevision(revision => revision + 1);
    }
  }, [gridId, userInfo]);

  const handleColumnResize = useCallback(({ column, width, size }) => {
    if (gridId && userInfo && column?.name) {
      updateColumnWidth(gridId, userInfo, column.name, width ?? size);
      setColumnPreferencesRevision(revision => revision + 1);
    }
  }, [gridId, userInfo]);

  const applyColumnWidthUpdates = useCallback((updates) => {
    if (!gridId || !userInfo || !updates?.length) return;

    updates.forEach(({ column, width }) => {
      if (column?.name) {
        updateColumnWidth(gridId, userInfo, column.name, width);
      }
    });
    setColumnPreferencesRevision(revision => revision + 1);
  }, [gridId, userInfo]);

  const renderColumnContextMenu = useCallback((menuProps, menuContext) => {
    const items = (menuProps.items || []).map(item => {
      const autosizeMode =
        item?.itemId === 'autoSizeToFit'
          ? 'fit'
          : item?.itemId === 'autoresizeThisColumn'
          ? 'this'
          : item?.itemId === 'autoresizeAllColumns'
          ? 'all'
          : null;

      if (!autosizeMode) return item;

      return {
        ...item,
        onClick: () => {
          const updates = getAutosizeColumnUpdates(
            menuContext.computedProps,
            menuContext.cellProps?.id,
            autosizeMode
          );
          applyColumnWidthUpdates(updates);
          menuContext.computedProps?.hideColumnContextMenu?.();
        }
      };
    });

    return <Menu {...menuProps} items={items} />;
  }, [applyColumnWidthUpdates]);

  const resolveEmptyText = () => {
    if (loading) return 'Loading data...';
    if (onFetchError && fetchFailed) return '';
    return emptyText !== undefined ? emptyText : 'No records available';
  };

  const gridStyle = useMemo(() => ({
    minHeight: 500,
    width: '100%'
  }), []);

  const handleDropdownToggle = useCallback((dropdownId) => (isOpen) => {
    setOpenDropdown(isOpen ? dropdownId : null);
  }, []);

  const displaySelectedCount = useMemo(() => {
    return isAllSelected(selectionRef)
      ? totalCount - Object.keys(selectionRef.unselected || {}).length
      : selectedCount;
  }, [selectionRef, totalCount, selectedCount]);

  const renderBulkActions = useMemo(() => {
    if (!bulkActions.length) return null;

    return bulkActions.map((action, index) => {
      const dropdownId = action.actionType || index;
      return (
        <BulkActionDropdown
          key={action.label || index}
          label={action.label}
          options={action.options}
          onSelect={(option) => handleBulkActionSelect(action.actionType, option)}
          disabled={!hasSelection}
          testID={`bulkAction-${action.actionType}`}
          isOpen={openDropdown === dropdownId}
          onToggle={handleDropdownToggle(dropdownId)}
          selectedCount={displaySelectedCount}
        />
      );
    });
  }, [bulkActions, hasSelection, handleBulkActionSelect, openDropdown, handleDropdownToggle, displaySelectedCount]);

  return (
    <View style={styles.container}>
      {!hideToolbar && <View style={styles.toolbar}>
        {hasSelection && (
          <View style={styles.bulkActionsRow}>
            <View style={styles.bulkActionsLeft}>
              <Text style={styles.selectedText}>
                {displaySelectedCount} {displaySelectedCount === 1 ? 'item' : 'items'} selected
              </Text>
            </View>
            <View style={styles.bulkActionsRight}>
              {renderBulkActions}
            </View>
          </View>
        )}

        <View
          style={[
            styles.searchFilterRow,
            windowWidth < 768 && styles.searchFilterRowMobile
          ]}>
          {typeFilterOptions.length > 0 && (
            <FilterDropdown
              label="Type"
              options={typeFilterOptions}
              selectedValue={activeTypeFilter}
              onSelect={handleTypeFilterChange}
              testID="typeFilterDropdown"
              isOpen={openDropdown === 'typeFilter'}
              onToggle={(isOpen) => setOpenDropdown(isOpen ? 'typeFilter' : null)}
            />
          )}
          {filterOptions.length > 0 && (
            <FilterDropdown
              label="Status"
              options={filterOptions}
              selectedValue={activeFilter}
              onSelect={handleFilterChange}
              testID="statusFilterDropdown"
              isOpen={openDropdown === 'statusFilter'}
              onToggle={(isOpen) => setOpenDropdown(isOpen ? 'statusFilter' : null)}
            />
          )}
          {toolbarRight && windowWidth < 768 && (
            <View style={styles.toolbarRightContainerMobile}>
              {toolbarRight}
            </View>
          )}
          <View
            style={[
              styles.searchBarContainer,
              windowWidth < 768 && styles.searchBarContainerMobile
            ]}>
            <SearchBar
              searchValue={searchValue}
              onSearchChange={handleSearchChange}
              onClear={clearSearch}
              placeholder={searchPlaceholder}
              windowWidth={windowWidth}
            />
          </View>
          {toolbarRight && windowWidth >= 768 && (
            <View style={styles.toolbarRightContainer}>{toolbarRight}</View>
          )}
        </View>
      </View>}

      <ReactDataGrid
        key={resetKey === undefined ? `grid-${debouncedValue}` : `grid-${effectiveSearch}-${resetKey}`}
        idProperty={idProperty}
        columns={gridColumns}
        dataSource={dataSource}
        style={gridStyle}
        pagination="remote"
        defaultLimit={DEFAULT_PAGE_SIZE}
        pageSizes={PAGE_SIZES}
        checkboxColumn
        selected={selectionRef.selected}
        unselected={selectionRef.unselected}
        onSelectionChange={handleGridSelectionChange}
        onRowClick={handleRowClick}
        onReady={(gridApiRef) => {
          gridRef.current = gridApiRef.current;
        }}
        onColumnVisibleChange={handleColumnVisibleChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnResize={handleColumnResize}
        reorderColumns={true}
        className="product-grid-container"
        loading={loading}
        loadingText="Loading data..."
        emptyText={resolveEmptyText()}
        showZebraRows={false}
        showColumnMenuTool={true}
        enableColumnAutosize={true}
        renderColumnContextMenu={renderColumnContextMenu}
        rowHeight={45}
        headerHeight={45}
        theme="default-dark"
        {...gridProps}
      />

      {/* Error display */}
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
    </View>
  );
});

ServerPaginatedDataGrid.displayName = 'ServerPaginatedDataGrid';

const styles = StyleSheet.create({
  container: {
    flex: 1
  },
  toolbar: {
    backgroundColor: '#2d3e4e',
    position: 'relative',
    zIndex: 100,
    overflow: 'visible'
  },
  bulkActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: 'rgb(38, 43, 48)',
    position: 'relative',
    zIndex: 99999,
    overflow: 'visible'
  },
  bulkActionsLeft: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  bulkActionsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    position: 'relative',
    zIndex: 99999,
    overflow: 'visible'
  },
  searchFilterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
    zIndex: 99998
  },
  searchBarContainer: {
    flex: 1
  },
  searchFilterRowMobile: {
    flexWrap: 'wrap'
  },
  searchBarContainerMobile: {
    flexBasis: '100%',
    marginTop: 8
  },
  toolbarRightContainer: {
    marginLeft: 10
  },
  toolbarRightContainerMobile: {
    marginLeft: 'auto'
  },
  selectedText: {
    color: '#7bfac3',
    fontSize: 14,
    fontFamily: fontFamily.font300
  },
  errorContainer: {
    backgroundColor: '#ff4444',
    padding: 10,
    borderRadius: 4,
    marginTop: 10
  },
  errorText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: fontFamily.font300
  }
});

export default ServerPaginatedDataGrid;
